import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowUp, ArrowDown, ChevronsUpDown, ChevronRight } from 'lucide-react'
import SearchBar from '../../../components/SearchBar'
import type { SelgerRow } from './api'
import { useBruktbilFilters } from '../useBruktbilFilters'
import { MerkeChips } from '../MerkeChips'

type SortCol = 'count' | 'avg_bilalder' | 'drivstoff_share'
type Sort = { col: SortCol; dir: 'asc' | 'desc' } | null

interface Props {
  rows: SelgerRow[]
  page: number
  pages: number
  total: number
  onPageChange: (page: number) => void
}

function SortIcon({ col, sort }: { col: SortCol; sort: Sort }) {
  if (!sort || sort.col !== col) return <ChevronsUpDown className="w-4 h-4 inline ml-1 opacity-40" />
  return sort.dir === 'asc'
    ? <ArrowUp className="w-4 h-4 inline ml-1" />
    : <ArrowDown className="w-4 h-4 inline ml-1" />
}

export default function SelgereTable({ rows, page, pages, total, onPageChange }: Props) {
  const { t } = useTranslation('bruktbil')
  const filters = useBruktbilFilters()
  const navigate = useNavigate()
  const [sort, setSort] = useState<Sort>(null)
  const [search, setSearch] = useState('')

  function handleSort(col: SortCol) {
    setSort(s => s?.col === col ? (s.dir === 'asc' ? { col, dir: 'desc' } : null) : { col, dir: 'asc' })
  }

  const filterQs = `from=${filters.from}&to=${filters.to}${filters.klasse.map(k => `&klasse=${k}`).join('')}${filters.varebil != null ? `&varebil=${filters.varebil}` : ''}${filters.omregistrering ? '&omregistrering=true' : ''}`

  const filtered = search.trim()
    ? rows.filter(r => r.org_name?.toLowerCase().includes(search.toLowerCase()) || r.org_nr?.includes(search))
    : rows

  const sorted = sort
    ? [...filtered].sort((a, b) => {
        const va = sort.col === 'count' ? a.count : sort.col === 'avg_bilalder' ? (a.avg_bilalder ?? -1) : (a.drivstoff_share ?? -1)
        const vb = sort.col === 'count' ? b.count : sort.col === 'avg_bilalder' ? (b.avg_bilalder ?? -1) : (b.drivstoff_share ?? -1)
        return sort.dir === 'asc' ? va - vb : vb - va
      })
    : filtered

  const totalCount = rows.reduce((s, r) => s + r.count, 0)

  return (
    <div className="card p-6">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          {sort && (
            <button onClick={() => setSort(null)} className="btn-secondary text-sm">
              {t('klasser.nullstillSortering')}
            </button>
          )}
        </div>
      </div>

      <div className="mb-4">
        <SearchBar
          value={search}
          onChange={v => setSearch(v)}
          placeholder={t('orgList.sokPlaceholder')}
        />
        {search && <span className="ml-3 text-xs text-gray-500">{t('orgList.nAvM', { n: filtered.length, m: rows.length })}</span>}
      </div>

      <div className="overflow-x-auto">
        <table className="data-table data-table-compact" data-testid="selgere-table">
          <thead>
            <tr>
              <th className="text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 whitespace-nowrap">{t('orgSammenligning.orgNr')}</th>
              <th className="text-left whitespace-nowrap">{t('orgList.postnr')}</th>
              {/* Navn takes the free space that Top 5 merker used to absorb (#594 AC2). */}
              <th className="text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 w-full">{t('orgList.navn')}</th>
              <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 whitespace-nowrap" onClick={() => handleSort('count')}>
                {t('common.antall')} {<SortIcon col="count" sort={sort} />}
              </th>
              <th className="text-left whitespace-nowrap">{t('orgList.top5Merker')}</th>
              <th className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 whitespace-nowrap" onClick={() => handleSort('avg_bilalder')}>
                {t('orgSammenligning.snittBilalder')} {<SortIcon col="avg_bilalder" sort={sort} />}
              </th>
              <th className="text-left cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 whitespace-nowrap" onClick={() => handleSort('drivstoff_share')}>
                {t('drivstoff.drivstoff')} {<SortIcon col="drivstoff_share" sort={sort} />}
              </th>
              <th className="text-center whitespace-nowrap">{t('klasser.handling')}</th>
            </tr>
          </thead>
          <tbody>
            <tr className="bg-primary-50 dark:bg-primary-900/20 font-bold border-t border-b border-primary-300 dark:border-primary-600">
              <td className="text-center py-2"><span className="text-primary-600 dark:text-primary-400 font-bold">∑</span></td>
              <td className="py-2"></td>
              <td className="font-bold text-primary-700 dark:text-primary-300 py-2">{t('klasser.total')}</td>
              <td className="text-right font-bold py-2">{totalCount.toLocaleString('nb-NO')}</td>
              <td className="py-2"></td>
              <td className="py-2"></td>
              <td className="py-2"></td>
              <td className="text-center py-2"><span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">—</span></td>
            </tr>
            {sorted.map((row, idx) => (
              <tr
                key={row.org_nr ?? idx}
                className={`${idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''} hover:bg-gray-100 dark:hover:bg-dark-border transition-colors cursor-pointer`}
                onClick={() => navigate(`/bruktbil/selgere/${row.org_nr}?${filterQs}`)}
              >
                <td className="font-mono text-xs text-primary-600 dark:text-primary-400">{row.org_nr ?? '—'}</td>
                <td className="font-mono text-xs text-gray-500">{row.postnummer ?? '—'}</td>
                <td className="font-bold">{row.org_name ?? '—'}</td>
                <td className="text-right">{row.count.toLocaleString('nb-NO')}</td>
                <td>
                  <MerkeChips merker={row.top_merker} />
                </td>
                <td className="text-right whitespace-nowrap">
                  {row.avg_bilalder != null ? t('units.aar', { value: row.avg_bilalder.toLocaleString('nb-NO', { maximumFractionDigits: 1 }) }) : '—'}
                </td>
                <td className="whitespace-nowrap">
                  {row.drivstoff_dominant
                    ? `${row.drivstoff_dominant}${row.drivstoff_share != null ? ' ' + Math.round(row.drivstoff_share * 100) + '%' : ''}`
                    : '—'}
                </td>
                <td className="text-center">
                  <button
                    onClick={e => { e.stopPropagation(); navigate(`/bruktbil/selgere/${row.org_nr}?${filterQs}`) }}
                    className="inline-flex items-center gap-1 whitespace-nowrap text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    <span className="text-xs font-medium">{t('common.seDetaljer')}</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sorted.length === 0 && (
        <p className="text-center text-gray-500 dark:text-gray-400 py-8">{t('klasser.ingenDataFunnet')}</p>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mt-4">
          <span>
            {t('pagination.sideAvTotalt', { page, pages, total: total.toLocaleString('nb-NO') })}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              {t('pagination.forrige')}
            </button>
            <button
              onClick={() => onPageChange(Math.min(pages, page + 1))}
              disabled={page === pages}
              className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              {t('pagination.neste')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
